import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../../hooks/use-country-config';
import { useSellerLabelProviders } from '../../../hooks/use-seller-label-providers';
import Global from '../../../store/global-selectors';
import GetCarrierDisplayName from '../../data-driven-logo/get-carrier-display-name';

export const preferredSellerProviderComparer = (sellerLabelProviderA, sellerLabelProviderB) => {
  const aIsPrimary = sellerLabelProviderA.isPrimary;
  const aIsWalleted = !!sellerLabelProviderA.walletId;
  const bisPrimary = sellerLabelProviderB.isPrimary;
  const bIsWalleted = !!sellerLabelProviderB.walletId;

  // Pick the primary provider
  if (aIsPrimary !== bisPrimary) {
    if (bisPrimary) {
      return 1;
    }
    return -1;
  }

  // If both are primary or neither are primary, pick the one that is walleted
  if (aIsWalleted !== bIsWalleted) {
    if (bIsWalleted) {
      return 1;
    }
    return -1;
  }

  // If neither of the above, choose the oldest one based on sellerLabelProviderId
  return parseInt(sellerLabelProviderA.sellerLabelProviderId, 10) - parseInt(sellerLabelProviderB.sellerLabelProviderId, 10);
};

const useLabelProviderIdToCarrierIdMap = () => {
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);
  return useMemo(
    () =>
      labelProviders.reduce((acc, labelProvider) => {
        return {
          ...acc,
          [labelProvider.labelProviderId]: labelProvider.carrierId,
        };
      }, {}),
    [labelProviders],
  );
};

const usePreferredCarrierSellerProviders = () => {
  const sellerProviders = useSellerLabelProviders();
  const labelProviderIdToCarrierIdMap = useLabelProviderIdToCarrierIdMap();
  return useMemo(() => {
    const sortedSellerLabelProviders = [...sellerProviders].sort(preferredSellerProviderComparer);

    return sortedSellerLabelProviders.reduce((groups, sellerLabelProvider) => {
      const carrierId = labelProviderIdToCarrierIdMap[sellerLabelProvider.labelProviderId];
      return {
        ...groups,
        [carrierId]: [...(groups[carrierId] || []), sellerLabelProvider.sellerLabelProviderId],
      };
    }, {});
  }, [sellerProviders, labelProviderIdToCarrierIdMap]);
};

const useServiceComparor = () => {
  const preferredCarrierSellerProviders = usePreferredCarrierSellerProviders();
  return useCallback(
    (serviceA, serviceB) => {
      const carrierList = preferredCarrierSellerProviders[serviceA.carrierId] || [];
      const serviceARank = carrierList.indexOf(serviceA.sellerLabelProviderId);
      const serviceBRank = carrierList.indexOf(serviceB.sellerLabelProviderId);

      return serviceBRank < serviceARank ? serviceB : serviceA;
    },
    [preferredCarrierSellerProviders],
  );
};

const useSellerProviderMap = () => {
  const sellerProviders = useSellerLabelProviders();
  return useMemo(() => {
    return sellerProviders.reduce((acc, sellerProvider) => {
      return {
        ...acc,
        [sellerProvider.sellerLabelProviderId]: sellerProvider,
      };
    }, {});
  }, [sellerProviders]);
};

const getCarrierSortOrder = (carrierId, preferredCarrierSellerProviders, sellerProviderMap, preferredCarrierId) => {
  if (preferredCarrierId && carrierId === preferredCarrierId) {
    return -1;
  }

  const carrierList = preferredCarrierSellerProviders[carrierId];
  if (!carrierList?.length) {
    return Infinity;
  }

  const sellerLabelProviderId = carrierList[0];
  return sellerProviderMap[sellerLabelProviderId]?.labelProviderSortOrder ?? Infinity;
};

const useCarrierGroups = (availableServices, selectedItem) => {
  const preferredCarrierSellerProviders = usePreferredCarrierSellerProviders();
  const carriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const sellerProviderMap = useSellerProviderMap();
  const [, preferredCarrierConfig] = useCountryConfig(CountryConfigNames.PreferredCarrier);
  const preferredCarrierId = preferredCarrierConfig?.carrierId ?? null;

  const sortedCarriers = carriers.sort((a, b) => {
    const carrierASortOrder = getCarrierSortOrder(a.carrierId, preferredCarrierSellerProviders, sellerProviderMap, preferredCarrierId);
    const carrierBSortOrder = getCarrierSortOrder(b.carrierId, preferredCarrierSellerProviders, sellerProviderMap, preferredCarrierId);
    return carrierASortOrder - carrierBSortOrder;
  });
  const choosePreferredService = useServiceComparor();
  const services = availableServices.filter((service) => !service.isHidden);
  const selectedServiceId = selectedItem?.serviceId;

  return sortedCarriers.map((carrier) => {
    const { carrierId } = carrier;
    const carrierServices = services.filter((service) => service.carrierId === carrierId);

    const preferredServices = Object.values(
      carrierServices.reduce((preferredServiceMap, service) => {
        const existingService = preferredServiceMap[service.carrierApiCode];
        return {
          ...preferredServiceMap,
          [service.carrierApiCode]: existingService
            ? choosePreferredService(preferredServiceMap[service.carrierApiCode], service)
            : service,
        };
      }, {}),
    );

    const items = preferredServices.map((service) => ({
      ...service,
      selected: service.serviceId === selectedServiceId,
    }));

    return {
      groupId: carrierId,
      name: GetCarrierDisplayName(carrierId),
      items,
    };
  });
};

export default useCarrierGroups;
